/**
 * Sponsored memberships on the business page (Sheepdog build, migration 2026-10-19-580). READ-ONLY.
 * Shows the sponsorship agreements this organization pays for (from Zuora: the household subscriptions whose invoice
 * owner is this organization's Zuora account, grouped by SponsorshipAgreementId) and the households covered.
 * Renders nothing for an organization with no agreements, so the ~2,800 other business pages are unchanged.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Card from "../ui/Card";
import { formatDateMDY } from "../../utils/formatters";
import {
  getOrgSponsorshipCredit,
  getOrgSponsoredMembers,
  getOrgSponsorshipAgreements,
  programDisplay,
} from "../../services/sponsorship.service";

const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  needs_review: "Needs review",
  ended: "Ended",
};
const statusChip = (s: string): string =>
  s === "active"
    ? "bg-green-100 text-green-800"
    : s === "needs_review"
      ? "bg-amber-100 text-amber-800"
      : "bg-gray-100 text-gray-600";

interface Props {
  organizationId: string;
}

const SponsoredMembershipsPanel: React.FC<Props> = ({ organizationId }) => {
  const [showMembers, setShowMembers] = useState(false);
  const agreements = useQuery({
    queryKey: ["sponsorship-agreements", organizationId],
    queryFn: () => getOrgSponsorshipAgreements(organizationId),
  });
  const credit = useQuery({
    queryKey: ["sponsorship-credit", organizationId],
    queryFn: () => getOrgSponsorshipCredit(organizationId),
    enabled: (agreements.data ?? []).length > 0,
  });
  const members = useQuery({
    queryKey: ["sponsorship-members", organizationId],
    queryFn: () => getOrgSponsoredMembers(organizationId),
    enabled: showMembers,
  });

  const rows = agreements.data ?? [];
  if (agreements.isLoading || agreements.isError || rows.length === 0) {
    return null;
  }

  return (
    <Card>
      <div
        className="flex flex-wrap items-center justify-between gap-2 mb-2"
        data-testid="sponsored-memberships-panel"
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Sponsored memberships
        </h3>
        <span className="text-xs text-gray-500">From Zuora · read-only</span>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Households whose membership this organization pays for. Sales credit for
        these memberships goes to this organization, not to an event or MSR.
      </p>
      <p className="text-sm mb-3" data-testid="sponsorship-credit">
        <span className="text-gray-500">Credited to: </span>
        {credit.data?.credited_to === "concierge" ? (
          <span className="font-medium text-gray-900">
            {credit.data.name} (Concierge)
            {credit.data.is_active === false ? " · inactive user" : ""}
          </span>
        ) : credit.isLoading ? (
          <span className="text-gray-500">…</span>
        ) : (
          <span className="text-amber-700">
            No Concierge assigned to this organization
          </span>
        )}
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Agreement</th>
              <th className="py-2 pr-4 font-medium">Program</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium text-right">Covered</th>
              <th
                className="py-2 pr-4 font-medium text-right"
                title="Seats bought, as recorded in Zuora"
              >
                Seats (Zuora)
              </th>
              <th className="py-2 pr-4 font-medium text-right">Ended</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.agreement_id} className="border-b last:border-0">
                <td className="py-2 pr-4 font-mono text-xs">
                  {a.agreement_ref}
                </td>
                <td className="py-2 pr-4">{programDisplay(a.program)}</td>
                <td className="py-2 pr-4">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusChip(a.status)}`}
                    title={
                      a.link_source === "staff"
                        ? "Linked to this organization by staff"
                        : "Linked automatically from the paying Zuora account"
                    }
                  >
                    {STATUS_LABEL[a.status] ?? a.status}
                  </span>
                </td>
                <td className="py-2 pr-4 text-right">{a.seats_covered}</td>
                <td className="py-2 pr-4 text-right">
                  {a.seat_allotment ?? "—"}
                </td>
                <td className="py-2 pr-4 text-right">{a.seats_ended}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="mt-3 text-sm text-primary-600 hover:text-primary-700 hover:underline"
        onClick={() => setShowMembers((v) => !v)}
      >
        {showMembers ? "Hide households" : "Show households"}
      </button>
      {showMembers && (
        <div className="mt-2" data-testid="sponsored-members-list">
          {members.isLoading && (
            <p className="text-sm text-gray-500">Loading…</p>
          )}
          {members.isError && (
            <p className="text-sm text-red-600">
              Could not load the households.
            </p>
          )}
          {members.data && members.data.length === 0 && (
            <p className="text-sm text-gray-500">No households yet.</p>
          )}
          {members.data && members.data.length > 0 && (
            <ul className="divide-y text-sm">
              {members.data.map((m) => (
                <li
                  key={`${m.agreement_ref}-${m.zuora_account_id}`}
                  className="py-1.5 flex flex-wrap items-center justify-between gap-2"
                >
                  <span>
                    {m.contact_id ? (
                      <Link
                        to={`/contacts/${m.contact_id}`}
                        className="text-primary-600 hover:underline"
                      >
                        {m.contact_name || m.account_number || "Member"}
                      </Link>
                    ) : (
                      <span>{m.account_number || m.zuora_account_id}</span>
                    )}
                    {m.account_number && (
                      <span className="ml-2 text-xs text-gray-500">
                        {m.account_number}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-600">
                    {m.covers
                      ? `Covered since ${m.since ? formatDateMDY(m.since) : "—"}`
                      : `Ended ${m.ended_on ? formatDateMDY(m.ended_on) : ""}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
};

export default SponsoredMembershipsPanel;
